import { DateTime } from 'luxon';
import { Lookup, RawGoal } from '@views';
import { palette } from '@app/palette';

export const MAX_ACTIVITIES = 3;
export const NO_ACTIVITY = -1;

// Hidden lookup ids the backend still expects on every goal.
export const TIMES_PER_WEEK_TIMEFRAME_ID = 1;
export const MINUTES_TIMEFRAME_ID = 2;

export const FREQUENCY_OPTIONS = [1, 2, 3, 4, 5, 6, 7];
export const DURATION_OPTIONS = Array.from({ length: 23 }, (_, i) => 10 + i * 5); // 10–120 min

// Brand teal, gold and blue, assigned by position in the goal so a person's (max 3) activities never
// collide. Color is decoration only — every card and dot also shows the activity's name.
export const ACTIVITY_COLORS = [palette.primary.main, palette.second.main, palette.first.main];

export type GoalActivity = {
  activityId: number;
  frequency: number;
  duration: number;
};

export const DEFAULT_FORM: GoalActivity = { activityId: NO_ACTIVITY, frequency: 3, duration: 30 };

export const colorForIndex = (index: number) => ACTIVITY_COLORS[index % ACTIVITY_COLORS.length];

/** True when the form's activity is already in the goal, ignoring the activity being edited. */
export const isDuplicateActivity = (activities: GoalActivity[], form: GoalActivity, editingIdx: number | null) =>
  form.activityId !== NO_ACTIVITY && activities.some((a, i) => a.activityId === form.activityId && i !== editingIdx);

/**
 * First Monday of the goal period the cohort is in. Goal periods cover weeks 1–4, 5–8 and 9–12.
 * Kronos computes the same period server-side; this is only for display and the request body.
 */
export const goalPeriodStart = (groupStartDate: DateTime, weekNumberIndexZero: number) => {
  if (weekNumberIndexZero < 5) {
    return groupStartDate.plus({ weeks: 1 }).startOf('week');
  }
  if (weekNumberIndexZero < 9) {
    return groupStartDate.plus({ weeks: 5 }).startOf('week');
  }
  return groupStartDate.plus({ weeks: 9 }).startOf('week');
};

export const goalPeriodEnd = (periodStart: DateTime) => periodStart.plus({ weeks: 4 }).minus({ days: 1 });

const lookupName = (lookups: Lookup[], id: number) => lookups.find((l) => l.id === id)?.name || '';

/** The goal card renders this text verbatim, so keep it identical to what the old dialog saved. */
export const buildGoalText = (activity: GoalActivity, activities: Lookup[], timeframes: Lookup[]) =>
  `I commit to ${lookupName(activities, activity.activityId)} ${activity.frequency} ${lookupName(
    timeframes,
    TIMES_PER_WEEK_TIMEFRAME_ID
  )} for ${activity.duration} ${lookupName(timeframes, MINUTES_TIMEFRAME_ID)} `;

export const toRawGoals = (
  goalActivities: GoalActivity[],
  context: { cohortUserId: number; startDate: DateTime; activities: Lookup[]; timeframes: Lookup[] }
): RawGoal[] =>
  goalActivities.map((activity) => ({
    activityId: activity.activityId,
    name: buildGoalText(activity, context.activities, context.timeframes),
    frequency: activity.frequency,
    duration: activity.duration,
    timeframeId: TIMES_PER_WEEK_TIMEFRAME_ID,
    durationTimeframeId: MINUTES_TIMEFRAME_ID,
    cohortUserId: context.cohortUserId,
    startDate: context.startDate,
    endDate: goalPeriodEnd(context.startDate),
  }));
